import React from "react";
import { Info } from "lucide-react";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import { Label } from "@/shared/components/ui/label";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import {
  RELATIONSHIP_OPTIONS,
  ONBOARDING_RELATIONSHIP_OPTIONS,
  normalizeStoredRelationship,
} from "@/shared/constants/relationshipOptions";
import { LEGAL_ROLE_OPTIONS } from "@/shared/constants/constants";
import { CustomMultiSelect } from "@/shared/components/CustomMultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

import type { ContactFormData, ContactFormErrors } from "@/shared/hooks/useContactForm";
import type { AddressParts } from "@/shared/utils/addressUtils";

interface ContactFormFieldsProps {
  formData: ContactFormData;
  errors: ContactFormErrors;
  onInputChange: (
    field: keyof Omit<ContactFormData, "address">,
    value: string | boolean | Array<{ value: string; label: string }>,
  ) => void;
  onAddressChange: (value: AddressParts) => void;
  relationshipLabel: string;
  deceasedFirstName?: string;
  isMeContact: boolean;
  portalContainer?: HTMLElement | null;
  legalRoleEditable?: boolean;
  readOnly?: boolean;
  hideAddress?: boolean;
  isAddMode?: boolean;
}

const ContactFormFields: React.FC<ContactFormFieldsProps> = ({
  formData,
  errors,
  onInputChange,
  onAddressChange,
  relationshipLabel,
  deceasedFirstName,
  isMeContact,
  portalContainer,
  legalRoleEditable = false,
  readOnly = false,
  hideAddress = false,
  isAddMode = false,
}) => {
  const possessive = deceasedFirstName ? `${deceasedFirstName}'s` : "the decedent's";
  const tooltipText = `This is the role assigned in ${possessive} legal documents, such as a will, trust, or court papers`;
  const labelClass = "block font-[400] text-sm text-stone-500";

  if (isAddMode) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-1.5">
            <Label htmlFor="firstName">First name</Label>
            <Input
              id="firstName"
              placeholder="John"
              value={formData.firstName}
              onChange={(e) => onInputChange("firstName", e.target.value)}
              className={errors.firstName ? "border-destructive" : ""}
            />
            <FieldError message={errors.firstName} className="mt-0" />
          </div>
          <div className="flex-1 flex flex-col gap-1.5">
            <Label htmlFor="middleName">Middle name</Label>
            <Input
              id="middleName"
              placeholder="Alexander"
              value={formData.middleName}
              onChange={(e) => onInputChange("middleName", e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="lastName">Last name</Label>
          <Input
            id="lastName"
            placeholder="Doe"
            value={formData.lastName}
            onChange={(e) => onInputChange("lastName", e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Relationship to {deceasedFirstName || "the decedent"}</Label>
          <Select
            value={formData.relationship}
            onValueChange={(value) => onInputChange("relationship", value)}
          >
            <SelectTrigger className="w-full font-normal">
              <SelectValue placeholder="Select relationship" />
            </SelectTrigger>
            <SelectContent>
              {ONBOARDING_RELATIONSHIP_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">
            Email{" "}
            {formData.inviteToElayne ? null : <span className="text-tertiary">(optional)</span>}
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="Email address"
            value={formData.email}
            onChange={(e) => onInputChange("email", e.target.value)}
            className={errors.email ? "border-destructive" : ""}
          />
          <FieldError message={errors.email} className="mt-0" />
        </div>

        <div className="flex gap-2 items-start">
          <div className="flex items-center h-[22px] shrink-0">
            <Checkbox
              checked={formData.inviteToElayne}
              onCheckedChange={(checked) => onInputChange("inviteToElayne", checked === true)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">Invite this person to Elayne</p>
            <p className="text-sm text-secondary">
              We'll email them an invitation when you add this contact. They'll be able to access{" "}
              {deceasedFirstName || "the decedent"}'s estate record.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-[24px]">
      <div>
        <Label htmlFor="fullName" className={`${labelClass} mt-[24px] mb-[4px]`}>
          Full name*
        </Label>
        <Input
          id="fullName"
          value={formData.fullName}
          onChange={(e) => onInputChange("fullName", e.target.value)}
          placeholder="John Smith"
          className={`w-full ${errors.fullName ? "border-destructive" : ""}`}
          disabled={readOnly}
        />
        <FieldError message={errors.fullName} className="mt-1" />
      </div>

      <div>
        <div className="flex items-center gap-1.5 mt-[24px] mb-[4px]">
          <Label className={labelClass}>Designated Role</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="hidden sm:inline-flex">
                  <Info className="h-3.5 w-3.5 text-stone-400" />
                </span>
              </TooltipTrigger>
              <TooltipContent>{tooltipText}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-2xs text-tertiary mb-1.5 sm:hidden">{tooltipText}</p>
        {legalRoleEditable ? (
          <CustomMultiSelect
            options={LEGAL_ROLE_OPTIONS}
            selected={formData.legalRole}
            onChange={(next) => onInputChange("legalRole", next)}
            placeholder="Select roles"
            disabled={readOnly}
          />
        ) : (
          <Input
            value={formData.legalRole.map((r) => r.label).join(", ") || ""}
            disabled
            placeholder="No role assigned"
            className="w-full bg-stone-50 text-stone-500 cursor-default"
          />
        )}
      </div>

      <div>
        <Label htmlFor="relationship" className={`${labelClass} mt-[24px] mb-[4px]`}>
          {relationshipLabel}
        </Label>
        <Select
          value={normalizeStoredRelationship(formData.relationship) ?? undefined}
          onValueChange={(value) => onInputChange("relationship", value)}
          disabled={readOnly}
        >
          <SelectTrigger className="w-full font-normal">
            <SelectValue placeholder="e.g., Brother, Friend, Colleague" />
          </SelectTrigger>
          <SelectContent>
            {RELATIONSHIP_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="email" className={`${labelClass} mt-[24px] mb-[4px]`}>
          Email
        </Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => onInputChange("email", e.target.value)}
          placeholder="example@email.com"
          className={`w-full ${errors.email ? "border-destructive" : ""}`}
          disabled={isMeContact || readOnly}
        />
        <FieldError message={errors.email} className="mt-1" />
      </div>

      <div>
        <Label htmlFor="phone" className={`${labelClass} mt-[24px] mb-[4px]`}>
          Phone number
        </Label>
        <PhoneNumberInput
          id="phone"
          value={formData.phone}
          onChange={(value) => onInputChange("phone", value)}
          className={`w-full ${errors.phone ? "border-destructive" : ""}`}
          disabled={readOnly}
        />
        <FieldError message={errors.phone} className="mt-1" />
      </div>

      {!hideAddress && (
        <div className={`mt-[24px] ${readOnly ? "pointer-events-none opacity-60" : ""}`}>
          <AddressPicker
            value={formData.address}
            onChange={onAddressChange}
            labels={{ streetAddress: "Address", apartment: "Apt, suite, etc." }}
            labelClassName={labelClass}
            stateFormat="abbreviation"
            portalContainer={portalContainer}
          />
        </div>
      )}
    </div>
  );
};

export default ContactFormFields;
